import React, { useState, useEffect } from "react";
import TableSummarySection from "./table-summary-section";
import TableCategoryGroup from "./table-category-group";
import {
  formatCurrency,
  formatPercent,
  calculateFinalPrice,
  getTotalDiscount,
  getDefaultSelectedOptions,
} from "./ctf-shared-utils";

/**
 * Main table view component for ClickToFly calculator
 * Displays comprehensive pricing data in a spreadsheet-like format
 * Updated for ClickToFlyResult model structure
 */
const ClickToFlyTableView = ({
  clickToFlyData,
  tripType,
  departureDate,
  departureAirportCode,
  arrivalAirportCode,
  estimateDoc,
}) => {
  // Sort aircraft options by lowest price (ascending)
  const sortedAircraftOptions = [...clickToFlyData.aircraftOptions].sort((a, b) => {
    const priceA = a.theoreticalMaxDiscount?.lowestPossiblePrice || a.defaultPrice || 0;
    const priceB = b.theoreticalMaxDiscount?.lowestPossiblePrice || b.defaultPrice || 0;
    return priceA - priceB;
  });

  // State for selected aircraft (defaults to 0 which is now the cheapest)
  const [selectedAircraftIndex, setSelectedAircraftIndex] = useState(0);

  // State for selected options (per aircraft)
  const [selectedOptionsByAircraft, setSelectedOptionsByAircraft] = useState(
    {}
  );

  const aircraft = sortedAircraftOptions[selectedAircraftIndex] || null;

  // Initialize selected options for the current aircraft
  useEffect(() => {
    if (aircraft && !selectedOptionsByAircraft[aircraft.aircraftTypeId]) {
      const defaultOptions = getDefaultSelectedOptions(aircraft);
      setSelectedOptionsByAircraft((prev) => ({
        ...prev,
        [aircraft.aircraftTypeId]: defaultOptions,
      }));
    }
  }, [aircraft, selectedOptionsByAircraft]);

  if (!aircraft) {
    return <div className="no-aircraft">No aircraft data available</div>;
  }

  const selectedOptions =
    selectedOptionsByAircraft[aircraft.aircraftTypeId] ||
    getDefaultSelectedOptions(aircraft);

  const calculatedPrice = calculateFinalPrice(aircraft, selectedOptions);
  const totalDiscount = getTotalDiscount(selectedOptions, aircraft.defaultPrice);

  // Handle option selection
  const handleOptionSelect = (category, option) => {
    setSelectedOptionsByAircraft((prev) => ({
      ...prev,
      [aircraft.aircraftTypeId]: {
        ...prev[aircraft.aircraftTypeId],
        [category]: option,
      },
    }));
  };

  // Prepare member options using toggleOptions structure
  const memberToggle = aircraft.toggleOptions?.member;
  const isMember = memberToggle?.isMember || false;

  const memberOptions = [
    {
      label: "Non-Member",
      value: false,
      change: memberToggle?.nonMemberDiscount || { percent: 0, dollars: 0 },
      isCurrent: !isMember,
    },
    {
      label: "Member",
      value: true,
      change: memberToggle?.memberDiscount || { percent: 0, dollars: 0 },
      isCurrent: isMember,
    },
  ];

  // Prepare TDR options
  const tdrOptions = aircraft.toggleOptions?.departureTdr?.available
    ? aircraft.toggleOptions.departureTdr.options || []
    : [];

  // Prepare airport radius options
  const flexibleAirportEnd = aircraft.toggleOptions?.alternateAirports?.flexibleAirportEnd;
  const airportRadiusOptions = (
    aircraft.toggleOptions?.alternateAirports?.options || []
  ).map((opt) => ({
    label: opt.radius === 0 ? "Exact Airport" : opt.label,
    value: opt.radius,
    radius: opt.radius,
    change: opt.change || { percent: 0, dollars: 0 },
    alternateInfo: {
      departureOptions: opt.departureAlternates || [],
      arrivalOptions: opt.arrivalAlternates || [],
    },
    flexibleEnd: flexibleAirportEnd,
    isExact: opt.radius === 0,
  }));

  // Prepare YOM options
  const yomOptions = (aircraft.toggleOptions?.yom?.options || []).map((opt) => ({
    label: `${opt.year}${opt.isDefault ? " (Default)" : ""}`,
    value: opt.year,
    year: opt.year,
    change: opt.change || { percent: 0, dollars: 0 },
    isIncrease: opt.isIncrease,
    isDefault: opt.isDefault,
    isDiscount: opt.isDiscount,
  }));

  // Prepare departure time options (per-leg for round trips)
  const departureTimeData = aircraft.toggleOptions?.departureTime;
  const isRoundTrip = tripType === 2;

  const mapDepartureTimeOptions = (options) =>
    (options || []).map((opt, idx) => ({
      label: opt.label,
      value: opt.type === "flexible" ? `${opt.type}-${idx}` : opt.type,
      change: opt.change || { percent: 0, dollars: 0 },
      timeWindow: opt.timeWindow,
      type: opt.type,
    }));

  // For OW: single options array, for RT: leg1Options and leg2Options
  const departureTimeLeg1Options = isRoundTrip
    ? mapDepartureTimeOptions(departureTimeData?.leg1Options)
    : mapDepartureTimeOptions(departureTimeData?.options);
  const departureTimeLeg2Options = isRoundTrip
    ? mapDepartureTimeOptions(departureTimeData?.leg2Options)
    : [];

  return (
    <div className="ctf-table-view">
      {/* Aircraft Selector */}
      <div className="aircraft-selector">
        <label htmlFor="aircraft-select">Select Aircraft:</label>
        <select
          id="aircraft-select"
          value={selectedAircraftIndex}
          onChange={(e) => setSelectedAircraftIndex(parseInt(e.target.value))}
        >
          {sortedAircraftOptions.map((ac, index) => (
            <option key={index} value={index}>
              {ac.aircraftTypeName} (
              {ac.seatCount ? `${ac.seatCount} seats` : "N/A"}
              ) - Starting from{" "}
              {formatCurrency(
                ac.theoreticalMaxDiscount?.lowestPossiblePrice || ac.defaultPrice
              )}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Section */}
      <TableSummarySection
        aircraft={aircraft}
        calculatedPrice={calculatedPrice}
        totalDiscount={totalDiscount}
        estimateDoc={estimateDoc}
        tripType={tripType}
      />

      {/* Main Toggle Table */}
      <div className="ctf-table-container">
        <table className="ctf-pricing-table">
          <thead>
            <tr>
              <th>Toggle</th>
              <th>Value</th>
              <th>Adj.</th>
              <th>Check</th>
              <th>Percent</th>
              <th>Dollars</th>
            </tr>
          </thead>
          <tbody>
            {/* Member Category */}
            <TableCategoryGroup
              categoryName="Member"
              categoryKey="member"
              options={memberOptions}
              selectedOption={selectedOptions.member}
              onOptionSelect={(option) => handleOptionSelect("member", option)}
              defaultPrice={aircraft.defaultPrice}
            />

            {/* Depart TDR Category */}
            {tdrOptions.length > 0 && (
              <TableCategoryGroup
                categoryName="Depart - TDR"
                categoryKey="tdr"
                options={tdrOptions}
                selectedOption={selectedOptions.tdr}
                onOptionSelect={(option) => handleOptionSelect("tdr", option)}
                defaultPrice={aircraft.defaultPrice}
              />
            )}

            {/* Airport Radius Category */}
            <TableCategoryGroup
              categoryName="Max - Radius"
              categoryKey="airportRadius"
              options={airportRadiusOptions}
              selectedOption={selectedOptions.airportRadius}
              onOptionSelect={(option) =>
                handleOptionSelect("airportRadius", option)
              }
              defaultPrice={aircraft.defaultPrice}
            />

            {/* YOM Category */}
            {yomOptions.length > 0 && (
              <TableCategoryGroup
                categoryName="YOM"
                categoryKey="yom"
                options={yomOptions}
                selectedOption={selectedOptions.yom}
                onOptionSelect={(option) => handleOptionSelect("yom", option)}
                defaultPrice={aircraft.defaultPrice}
              />
            )}

            {/* Departure Time Category - Per-leg for round trips */}
            {isRoundTrip ? (
              <>
                <TableCategoryGroup
                  categoryName="Departure Time - Leg 1"
                  categoryKey="departureTimeLeg1"
                  options={departureTimeLeg1Options}
                  selectedOption={selectedOptions.departureTimeLeg1}
                  onOptionSelect={(option) =>
                    handleOptionSelect("departureTimeLeg1", option)
                  }
                  defaultPrice={aircraft.defaultPrice}
                />
                <TableCategoryGroup
                  categoryName="Departure Time - Leg 2"
                  categoryKey="departureTimeLeg2"
                  options={departureTimeLeg2Options}
                  selectedOption={selectedOptions.departureTimeLeg2}
                  onOptionSelect={(option) =>
                    handleOptionSelect("departureTimeLeg2", option)
                  }
                  defaultPrice={aircraft.defaultPrice}
                />
              </>
            ) : (
              <TableCategoryGroup
                categoryName="Departure Time"
                categoryKey="departureTimeLeg1"
                options={departureTimeLeg1Options}
                selectedOption={selectedOptions.departureTimeLeg1}
                onOptionSelect={(option) =>
                  handleOptionSelect("departureTimeLeg1", option)
                }
                defaultPrice={aircraft.defaultPrice}
              />
            )}

            {/* Total Row */}
            <tr className="total-row">
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td className="total-percent">
                {formatPercent(totalDiscount.percent)}
              </td>
              <td className="total-dollar">
                -{formatCurrency(totalDiscount.dollars)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClickToFlyTableView;
